/**
 * Контекст платформенной сессии для файловых HTTP-запросов (вложения, `FILEDOWNLOAD`).
 *
 * Единые правила с клонами вкладок: `isClone` / `cloneOf` берутся из `pageData`, не из заглушек.
 *
 * @see dev/WUI-293.md — п. 16
 */
import { normalizePlatformSessionId } from '@/common/utils/jsRequestSessionContext'

/** Скаляр sessionId из записи `pagesData` / dialog (в т.ч. `{ type, value }`). */
export function readPlatformSessionIdFromPageEntry(entry: unknown): string | null {
  if (!entry || typeof entry !== 'object') return null

  const record = entry as Record<string, unknown>
  const fromSession = normalizePlatformSessionId(unwrapPageSessionScalar(record.sessionId))
  if (fromSession) return fromSession

  const fromAlias = normalizePlatformSessionId(unwrapPageSessionScalar(record.$sessionId))
  if (fromAlias) return fromAlias

  const fromClone = normalizePlatformSessionId(unwrapPageSessionScalar(record.cloneOf))
  if (fromClone) return fromClone

  return normalizePlatformSessionId(unwrapPageSessionScalar(record.$cloneOf))
}

function unwrapPageSessionScalar(value: unknown): unknown {
  let current = value
  for (let depth = 0; depth < 3; depth += 1) {
    if (current == null) return current
    if (typeof current === 'object' && 'value' in (current as object)) {
      current = (current as { value: unknown }).value
      continue
    }
    break
  }
  return current
}
/** Параметры сессии для `GET /platform/download` и `POST /platform/upload`. */
export type PlatformFileSessionContext = {
  sessionId: string
  isClone: boolean
  cloneOf: string | null
  type?: string
}

/**
 * Собирает контекст upload/download для текущей вкладки формы.
 *
 * @param pageData — слот страницы (`isClone`, `cloneOf`); строка `"null"` в `cloneOf` нормализуется в `null`
 * @param sessionId — id сессии core для этой вкладки
 * @param platformType — тип платформы формы (если есть)
 */
export function getPlatformFileSessionContext(
  pageData: { isClone?: boolean; cloneOf?: string | null } | null | undefined,
  sessionId: string,
  platformType?: string | null,
): PlatformFileSessionContext {
  const cloneOf = pageData?.cloneOf
  return {
    sessionId,
    isClone: pageData?.isClone === true,
    cloneOf:
      cloneOf != null && cloneOf !== '' && String(cloneOf).toLowerCase() !== 'null'
        ? String(cloneOf)
        : null,
    type: platformType ?? undefined,
  }
}
